import * as React from 'react';
import { isEmpty, isObject, isString, round } from 'lodash';

import SortableTable from 'components/sortable-table/SortableTable';

import { __t, applyTranslations } from 'utils/language';
import { addThousandsSeparator } from 'utils/helper/common';
import { insertIf } from 'utils/list';
import {
  DATA_COLUMNS_INSTAGRAM,
  sanitizeMessage
} from 'modules/insights/reports/utils/insights-reports';

import './insights-reports-analysis-summary.scss';

import type { SortableTableColumnType } from 'types';
import type {
  AnalysisSummaryTable,
  AnalysisTableData,
  ReportType
} from 'modules/insights/reports/types';

applyTranslations(require('locales/common/collabary-common.i18n.json'));
applyTranslations(require('modules/insights/reports/shared/insights-reports-shared.i18n.json'));
applyTranslations(require('./insights-reports-analysis-summary.i18n.json'));

type Props = {
  summaryTable: AnalysisSummaryTable,
  reportType: ReportType
};

const COMMON_MSG_KEY = 'collabary_common';
const SHARED_MSG_KEY = 'insights_reports_shared';
const MSG_KEY = 'insights_reports_analysis_summary';

class InsightsReportsAnalysisSummary extends React.PureComponent<Props> {
  getFooterTotal(columnName: string, data: Array<AnalysisTableData>): number {
    const getCount = (item) => (isObject(item[columnName]) ? Math.floor(item[columnName].count) : 0);

    return data.reduce((acc, item) => acc + getCount(item), 0);
  }

  getFooterData(data: Array<AnalysisTableData>): Array<any> {
    const elementCount = data.length || 1;
    const engRateCalculationValues: Record<string, number> = {};

    const totals = DATA_COLUMNS_INSTAGRAM.map(columnName => {
      const total = this.getFooterTotal(columnName, data);

      if (columnName === 'followers' || columnName === 'engagements') {
        engRateCalculationValues[columnName] = total;
      }

      const audienceProp = 'followers';

      return {
        key: columnName,
        total: addThousandsSeparator(total),
        avg: columnName === 'engagementRate'
          ? (engRateCalculationValues.engagements / engRateCalculationValues[audienceProp]) * 100
          : total / elementCount
      };
    });

    return [{ total: 100 }, ...totals];
  }

  getColumnTitle(): SortableTableColumnType {
    return {
      key: 'insights-reports-analysis-summary-title',
      title: __t(`${SHARED_MSG_KEY}.labels.query`),
      width: 150,
      render: (col) => col.searchTerm,
      renderFooter: (col) => (
        <span className="insights-reports-analysis-summary__cell-contents--total">
          {`Total (${col.total}%)`}
        </span>
      ),
      sort: (a, b) => a.searchTerm.localeCompare(b.searchTerm)
    };
  }

  getColumnCreators(): SortableTableColumnType {
    const { reportType } = this.props;

    return {
      key: 'insights-reports-analysis-content-creators',
      title: __t(`${COMMON_MSG_KEY}.kpis.content_creators`),
      titleTooltipMsg: __t(`${MSG_KEY}.${reportType}.tooltips.content_creators`),
      width: 136,
      render: (col) => (
        <div className="insights-reports-analysis-summary__cell-contents">
          <div className="insights-reports-analysis-summary__cell-contents--value">
            {addThousandsSeparator(Math.floor(col.contentCreators.count))}
          </div>
          <div className="insights-reports-analysis-summary__cell-contents--percentage">
            ({
              col.contentCreators.percentage < 10 ?
                col.contentCreators.percentage.toFixed(1) :
                Math.round(col.contentCreators.percentage)
            }%)
          </div>
        </div>
      ),
      renderFooter: (col) => (
        <div className="insights-reports-analysis-summary__cell-contents">
          <div className="insights-reports-analysis-summary__cell-contents--value">
            {col.total}
          </div>
        </div>
      ),
      sort: (a, b) => a.contentCreators.count - b.contentCreators.count
    };
  }

  getColumnFollowers(): SortableTableColumnType {
    const { reportType } = this.props;

    return {
      key: 'insights-reports-analysis-summary-followers',
      title: __t(`${COMMON_MSG_KEY}.kpis.followers`),
      titleTooltipMsg: __t(`${MSG_KEY}.${reportType}.tooltips.followers`),
      width: 136,
      render: (col) => (
        <div className="insights-reports-analysis-summary__cell-contents">
          <div className="insights-reports-analysis-summary__cell-contents--value">
            {addThousandsSeparator(Math.floor(col.followers.count))}
          </div>
          <div className="insights-reports-analysis-summary__cell-contents--percentage">
            ({
              col.followers.percentage < 10 ?
                col.followers.percentage.toFixed(1) :
                Math.round(col.followers.percentage)
            }%)
          </div>
        </div>
      ),
      renderFooter: (col) => (
        <div className="insights-reports-analysis-summary__cell-contents">
          <div className="insights-reports-analysis-summary__cell-contents--value">
            {col.total}
          </div>
        </div>
      ),
      sort: (a, b) => a.followers.count - b.followers.count
    };
  }

  getColumnSubscribers(): SortableTableColumnType {
    const { reportType } = this.props;

    return {
      key: 'insights-reports-analysis-summary-subscribers',
      title: __t(`${COMMON_MSG_KEY}.kpis.subscribers`),
      titleTooltipMsg: __t(`${MSG_KEY}.${reportType}.tooltips.subscribers`),
      width: 136,
      render: (col) => (
        <div className="insights-reports-analysis-summary__cell-contents">
          <div className="insights-reports-analysis-summary__cell-contents--value">
            {addThousandsSeparator(Math.floor(col.subscribers.count))}
          </div>
          <div className="insights-reports-analysis-summary__cell-contents--percentage">
            ({
              col.subscribers.percentage < 10 ?
                col.subscribers.percentage.toFixed(1) :
                Math.round(col.subscribers.percentage)
            }%)
          </div>
        </div>
      ),
      renderFooter: (col) => (
        <div className="insights-reports-analysis-summary__cell-contents">
          <div className="insights-reports-analysis-summary__cell-contents--value">
            {col.total}
          </div>
        </div>
      ),
      sort: (a, b) => a.subscribers.count - b.subscribers.count
    };
  }

  getColumnPosts(): SortableTableColumnType {
    const { reportType } = this.props;
    let title = __t(`${COMMON_MSG_KEY}.kpis.posts`);

    title = isString(title) ? title.replace(/\sposted/, '') : '';

    return {
      key: 'insights-reports-analysis-summary-posts',
      title,
      titleTooltipMsg: __t(`${MSG_KEY}.${reportType}.tooltips.posts`),
      width: 136,
      render: (col) => (
        <div className="insights-reports-analysis-summary__cell-contents">
          <div className="insights-reports-analysis-summary__cell-contents--value">
            {addThousandsSeparator(Math.floor(col.posts.count))}
          </div>
          <div className="insights-reports-analysis-summary__cell-contents--percentage">
            ({
              col.posts.percentage < 10 ?
                col.posts.percentage.toFixed(1) :
                Math.round(col.posts.percentage)
            }%)
          </div>
        </div>
      ),
      renderFooter: (col) => (
        <div className="insights-reports-analysis-summary__cell-contents">
          <div className="insights-reports-analysis-summary__cell-contents--value">
            {col.total}
          </div>
        </div>
      ),
      sort: (a, b) => a.posts.count - b.posts.count
    };
  }

  getColumnImpressions(): SortableTableColumnType {
    const { reportType } = this.props;

    return {
      key: 'insights-reports-analysis-summary-impressions',
      title: __t(`${COMMON_MSG_KEY}.kpis.impressions`),
      titleTooltipMsg: __t(`${MSG_KEY}.${reportType}.tooltips.impressions`),
      width: 136,
      render: (col) => (
        <div className="insights-reports-analysis-summary__cell-contents">
          <div className="insights-reports-analysis-summary__cell-contents--value">
            {addThousandsSeparator(Math.floor(col.impressions.count))}
          </div>
          <div className="insights-reports-analysis-summary__cell-contents--percentage">
            ({
              col.impressions.percentage < 10 ?
                col.impressions.percentage.toFixed(1) :
                Math.round(col.impressions.percentage)
            }%)
          </div>
        </div>
      ),
      renderFooter: (col) => (
        <div className="insights-reports-analysis-summary__cell-contents">
          <div className="insights-reports-analysis-summary__cell-contents--value">
            {col.total}
          </div>
        </div>
      ),
      sort: (a, b) => a.impressions.count - b.impressions.count
    };
  }

  getColumnReach(): SortableTableColumnType {
    const { reportType } = this.props;

    return {
      key: 'insights-reports-analysis-summary-reach',
      title: __t(`${COMMON_MSG_KEY}.kpis.reach`),
      titleTooltipMsg: __t(`${MSG_KEY}.${reportType}.tooltips.reach`),
      width: 136,
      render: (col) => (
        <div className="insights-reports-analysis-summary__cell-contents">
          <div className="insights-reports-analysis-summary__cell-contents--value">
            {addThousandsSeparator(Math.floor(col.reach.count))}
          </div>
          <div className="insights-reports-analysis-summary__cell-contents--percentage">
            ({
              col.reach.percentage < 10 ?
                col.reach.percentage.toFixed(1) :
                Math.round(col.reach.percentage)
            }%)
          </div>
        </div>
      ),
      renderFooter: (col) => (
        <div className="insights-reports-analysis-summary__cell-contents">
          <div className="insights-reports-analysis-summary__cell-contents--value">
            {col.total}
          </div>
        </div>
      ),
      sort: (a, b) => a.reach.count - b.reach.count
    };
  }

  getColumnViews(): SortableTableColumnType {
    const { reportType } = this.props;

    return {
      key: 'insights-reports-analysis-summary-views',
      title: __t(`${COMMON_MSG_KEY}.kpis.views`),
      titleTooltipMsg: __t(`${MSG_KEY}.${reportType}.tooltips.views`),
      width: 136,
      render: (col) => (
        <div className="insights-reports-analysis-summary__cell-contents">
          <div className="insights-reports-analysis-summary__cell-contents--value">
            {addThousandsSeparator(Math.floor(col.views.count))}
          </div>
          <div className="insights-reports-analysis-summary__cell-contents--percentage">
            ({
              col.views.percentage < 10 ?
                col.views.percentage.toFixed(1) :
                Math.round(col.views.percentage)
            }%)
          </div>
        </div>
      ),
      renderFooter: (col) => (
        <div className="insights-reports-analysis-summary__cell-contents">
          <div className="insights-reports-analysis-summary__cell-contents--value">
            {col.total}
          </div>
        </div>
      ),
      sort: (a, b) => a.views.count - b.views.count
    };
  }

  getColumnEngagement(): SortableTableColumnType {
    const { reportType } = this.props;

    return {
      key: 'insights-reports-analysis-summary-engagement',
      title: __t(`${COMMON_MSG_KEY}.kpis.engagements`),
      titleTooltipMsg: __t(`${MSG_KEY}.${reportType}.tooltips.engagements`),
      width: 136,
      render: (col) => (
        <div className="insights-reports-analysis-summary__cell-contents">
          <div className="insights-reports-analysis-summary__cell-contents--value">
            {addThousandsSeparator(Math.floor(col.engagements.count))}
          </div>
          <div className="insights-reports-analysis-summary__cell-contents--percentage">
            ({
              col.engagements.percentage < 10 ?
                col.engagements.percentage.toFixed(1) :
                Math.round(col.engagements.percentage)
            }%)
          </div>
        </div>
      ),
      renderFooter: (col) => (
        <div className="insights-reports-analysis-summary__cell-contents">
          <div className="insights-reports-analysis-summary__cell-contents--value">
            {col.total}
          </div>
        </div>
      ),
      sort: (a, b) => a.engagements.count - b.engagements.count
    };
  }

  getColumnEngagementRate(): SortableTableColumnType {
    const { reportType } = this.props;

    return {
      key: 'insights-reports-analysis-summary-engagement-rate',
      title: __t(`${COMMON_MSG_KEY}.kpis.engagement_rate`),
      titleTooltipMsg: __t(`${MSG_KEY}.${reportType}.tooltips.engagement_rate`),
      width: 136,
      render: (col) => (
        <div className="insights-reports-analysis-summary__cell-contents">
          <div className="insights-reports-analysis-summary__cell-contents--value">
            {round(col.engagementRate.count, 1)}%
          </div>
        </div>
      ),
      renderFooter: (col) => (
        <div className="insights-reports-analysis-summary__cell-contents">
          <div className="insights-reports-analysis-summary__cell-contents--value">
            <span className="insights-reports-analysis-summary__cell-contents--info">[Avg]</span>
            {round(col.avg, 1)}%
          </div>
        </div>
      ),
      sort: (a, b) => a.engagementRate.count - b.engagementRate.count
    };
  }

  render() {
    const { summaryTable } = this.props;

    const uniqueInfluencersMessage = __t(`${SHARED_MSG_KEY}.messages.unique_mentions`, {
      uniqueInfluencers: addThousandsSeparator(summaryTable.uniqueContentCreators)
    });

    const tableItems = summaryTable.tableItems || [];

    const columns = [
      ...insertIf(!isEmpty(tableItems), ...[
        this.getColumnTitle(),
        this.getColumnCreators(),
        this.getColumnFollowers(),
        this.getColumnPosts(),
        this.getColumnImpressions(),
        this.getColumnReach(),
        this.getColumnEngagement(),
        this.getColumnEngagementRate()
      ])
    ];

    return (
      <div className="insights-reports-analysis-summary__results">
        <div className="insights-reports-analysis-summary__results-tables">
          <SortableTable
            data={tableItems}
            dataFooter={this.getFooterData(tableItems)}
            columns={columns}
            layout="standard"
            customClass="insights-reports-analysis-summary__sortable-table"
          />
        </div>
        <div
          className="insights-reports-analysis-summary__results-overview"
          dangerouslySetInnerHTML={{
            __html: sanitizeMessage(uniqueInfluencersMessage)
          }}
        />
      </div>
    );
  }
}

export default InsightsReportsAnalysisSummary;
